import type { PrintConfig } from '@matix/types';
import { POPUP_BLOCKED } from './printReceipt.js';

/**
 * A4 invoices used to come off the report server. They no longer do.
 *
 * When a tenant turns on `PrintReceiptA4`, the document they expect is the one
 * the ERP team's RDL produces on SSRS — the same one the legacy Sismatix POS
 * opened in a popup (`/SSRS/ReportViewer.aspx?q=P_S_52_CashSales&ID=…`).
 * pos-api can render it (the report server speaks NTLM over plain HTTP, so a
 * browser cannot) and this module opens the PDF in a tab, the way the legacy
 * app did.
 *
 * That round-trip is too slow to print behind a counter: a cold render took
 * 34-40 s, and even warm — with the keep-warm ping and the PDF cache — the
 * first sheet of a shift made the cashier wait. So `buildA4Html` now draws the
 * same sheet locally, measured off that report, and prints instantly.
 *
 * Everything below is kept whole and wired: the server routes still render the
 * report, and flipping `REPORT_INVOICE_ENABLED` back to true is all it takes to
 * put a tenant back on it if their RDL ever diverges from our sheet.
 */

/**
 * Master switch for the report-server A4 path. Off: every caller falls through
 * to `buildReceiptHtml`, which lays the invoice out itself.
 */
const REPORT_INVOICE_ENABLED = false;

/** True when this tenant's A4 invoice must come from the report server. */
export function usesReportInvoice(printConfig: PrintConfig): boolean {
  return REPORT_INVOICE_ENABLED && printConfig.printOptions?.a4 === true;
}

/** How long the blob URL stays alive after the tab has been pointed at it. */
const BLOB_TTL_MS = 60_000;

/** Placeholder shown while the report server is rendering. */
const PLACEHOLDER = `<!doctype html><html><head><meta charset="utf-8">
<title>Invoice · فاتورة</title></head>
<body style="margin:0;display:flex;align-items:center;justify-content:center;
height:100vh;font:16px system-ui,sans-serif;color:#475569;background:#f8fafc">
Preparing the invoice… · جارٍ تجهيز الفاتورة…
</body></html>`;

/**
 * Fetch the invoice PDF and show it in a new tab.
 *
 * The tab is opened *before* the fetch, synchronously inside the caller's event
 * turn: `window.open` after an await is what pop-up blockers stop. If the
 * browser blocks it anyway this rejects with `POPUP_BLOCKED`, the same signal
 * `printReceipt` uses, so callers keep one error path.
 *
 * Anything the fetch throws is rethrown after the placeholder tab is closed —
 * a tenant printing a legal invoice must see the failure, never a silently
 * different document.
 */
export async function printReportInvoice(fetchPdf: () => Promise<Blob>): Promise<void> {
  if (typeof window === 'undefined') {
    throw new Error('printReportInvoice: no window — must be called in the browser');
  }

  const win = window.open('', '_blank');
  if (!win) throw new Error(POPUP_BLOCKED);

  try {
    win.document.open();
    win.document.write(PLACEHOLDER);
    win.document.close();
  } catch {
    // A browser that refuses document.write on the new tab still navigates
    // fine below; the user just sees a blank tab for a moment.
  }

  let blob: Blob;
  try {
    blob = await fetchPdf();
  } catch (err) {
    try { win.close(); } catch { /* already gone */ }
    throw err;
  }

  const url = URL.createObjectURL(blob);
  try {
    win.location.href = url;
  } catch {
    URL.revokeObjectURL(url);
    throw new Error('printReportInvoice: could not open the rendered invoice');
  }
  // The tab owns the URL now; release it once the viewer has certainly read it.
  window.setTimeout(() => URL.revokeObjectURL(url), BLOB_TTL_MS);
}
